import { useMemo } from 'react';

/**
 * Custom hook to get userAnswer from localStorage
 * Used by SingleCorrectAnswerReview to reduce component size
 */
export function useUserAnswerFromStorage(question) {
  return useMemo(() => {
    try {
      // Get soal-id and proset-id from multiple sources
      const originalQuestion = question?.originalQuestion || question;
      const soalId = originalQuestion?.['soal-id'] || originalQuestion?.soalId || originalQuestion?.id ||
                     question?.['soal-id'] || question?.soalId || question?.id;
      const prosetId = originalQuestion?.['proset-id'] || originalQuestion?.prosetId || originalQuestion?.proset_id ||
                       question?.['proset-id'] || question?.prosetId || question?.proset_id;
      
      if (!soalId && !prosetId) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('⚠️ useUserAnswerFromStorage - No soal-id or proset-id found');
        }
        return null;
      }
      
      // Try qp-result first
      const qpResultRaw = localStorage.getItem("qp-result");
      if (qpResultRaw) {
        const parsed = JSON.parse(qpResultRaw);
        const userAnswers = parsed.userAnswers || [];
        const matchedAnswer = userAnswers.find(ua => {
          const uaSoalId = ua?.['soal-id'] || ua?.soalId || ua?.soal_id;
          const uaProsetId = ua?.['proset-id'] || ua?.prosetId || ua?.proset_id;
          return (soalId && uaSoalId === soalId) || (prosetId && uaProsetId === prosetId);
        });
        if (matchedAnswer) {
          return matchedAnswer['user-answer'] ?? matchedAnswer.userAnswer ?? matchedAnswer.answer;
        }
      }
      
      // Try user-answers
      const userAnswersRaw = localStorage.getItem("user-answers");
      if (userAnswersRaw) {
        const parsed = JSON.parse(userAnswersRaw);
        const userAnswersArray = Array.isArray(parsed) 
          ? parsed 
          : (parsed['user-answers'] || []);
        
        // Try to match by soal-id first, then proset-id
        let matchedAnswer = null;
        if (soalId) {
          matchedAnswer = userAnswersArray.find(ua => {
            const uaSoalId = ua?.['soal-id'] || ua?.soalId || ua?.soal_id;
            return uaSoalId === soalId;
          });
        }
        
        if (!matchedAnswer && prosetId) {
          matchedAnswer = userAnswersArray.find(ua => {
            const uaProsetId = ua?.['proset-id'] || ua?.prosetId || ua?.proset_id;
            return uaProsetId === prosetId;
          });
        }
        
        if (matchedAnswer) {
          return matchedAnswer['user-answer'] ?? matchedAnswer.userAnswer ?? matchedAnswer.answer;
        } else {
          if (process.env.NODE_ENV === 'development') {
            console.warn('⚠️ useUserAnswerFromStorage - No matching answer found:', {
              soalId,
              prosetId,
              availableSoalIds: userAnswersArray.map(ua => ua?.['soal-id'] || ua?.soalId || ua?.soal_id),
              availableProsetIds: userAnswersArray.map(ua => ua?.['proset-id'] || ua?.prosetId),
            });
          }
        }
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.warn('⚠️ useUserAnswerFromStorage - user-answers not found in localStorage');
        }
      }
      
      return null;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ useUserAnswerFromStorage - Error loading from localStorage:', error);
      }
      return null;
    }
  }, [question]);
}

